import { computed, type Ref, type Reactive } from "vue";
import {
  CognitoUserCreateInput,
  CognitoUserCountInput,
  ShopListInput,
  GetCognitoUserQuery,
} from "@/generated/graphql";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import {
  checkChainListDisplay,
  isOperatorGroupAdmin,
} from "@/common/business";
import { OperationGroupList, OperationAuthTypeList } from "@/common/constant";
import operatorStore from "@/store/operatorStore";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  OPERATOR_REGISTER_FILE_NAME,
  REQUIRED_FIELD_LABEL_KEYS,
} from "../constants";
import type { RadioItem, ChainItem, ShopItem } from "../types";

/** オペレータ登録 初期化処理に必要な依存をまとめた型。 */
interface UseOperatorRegisterInitDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  updateMode: Ref<boolean>;
  wordList: Ref<Record<string, string>>;
  chainListItems: Ref<ChainItem[]>;
  shopListItems: Ref<ShopItem[]>;
  cognitUserCreateInput: Reactive<CognitoUserCreateInput>;
  cognitoUserCountInput: Reactive<CognitoUserCountInput>;
  shopListInput: Reactive<ShopListInput>;
  chainSelectValues: Ref<{ chain_code: string; name: string } | null | undefined>;
  shopSelectValues: Ref<{ shop_code: string; name: string } | null | undefined>;
  groupSelectValues: Ref<RadioItem>;
  authtypeSelectValues: Ref<RadioItem>;
  personalSelectValues: Ref<RadioItem>;
  personalDlSelectValues: Ref<RadioItem>;
  selectedOperatorGroupName: Ref<string | undefined>;
  selectedOperatorChainName: Ref<string | undefined>;
  update_mode_operator_email: Ref<string>;
  email_required: Ref<number>;
  tel_required: Ref<number>;
  isIpRestrictedChain: Ref<boolean>;
  chainViewFlag: Ref<boolean>;
  personalRadioItems: RadioItem[];
  operatorFormRef: Ref<any>;
  getWordListData: (chainCode: string, screenId: string, customerCode: string) => Promise<Record<string, string>>;
  getChainListData: (customerCode: string) => Promise<ChainItem[] | null | undefined>;
  getShopListData: (input: ShopListInput) => Promise<ShopItem[] | null | undefined>;
  getChainInfoData: (customerCode: string, chainCode: string) => Promise<any>;
  getCognitoUserData: (
    input: { chain_code: string; customer_code: string; user_id: string },
    logInput: { area_code: number; detail: string; display_category: string; operation: string; user_id: string }
  ) => Promise<ApolloQueryResult<GetCognitoUserQuery>>;
  createGroupRadio: () => Promise<void>;
  onClickGroup: (index: string) => Promise<void>;
  setChainCode: (chainCode: string, selectUpdate: boolean) => Promise<void>;
  setShopCode: (shopCode: string) => Promise<void>;
  checkIpRestricted: (chainCode: string) => boolean;
  syncSelectionFields: () => void;
}

/**
 * オペレータ登録 初期化処理を扱う composable。
 * 画面初期ロード（onMounted 処理の委譲先）を管理する。
 */
export const useOperatorRegisterInit = (deps: UseOperatorRegisterInitDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    updateMode,
    wordList,
    chainListItems,
    shopListItems,
    cognitUserCreateInput,
    cognitoUserCountInput,
    shopListInput,
    chainSelectValues,
    shopSelectValues,
    groupSelectValues,
    authtypeSelectValues,
    personalSelectValues,
    personalDlSelectValues,
    selectedOperatorGroupName,
    selectedOperatorChainName,
    update_mode_operator_email,
    email_required,
    tel_required,
    isIpRestrictedChain,
    chainViewFlag,
    personalRadioItems,
    operatorFormRef,
    getWordListData,
    getChainListData,
    getShopListData,
    getChainInfoData,
    getCognitoUserData,
    createGroupRadio,
    onClickGroup,
    setChainCode,
    setShopCode,
    checkIpRestricted,
    syncSelectionFields,
  } = deps;

  // wordListが準備できているかをチェック（必要なラベルキーが全て存在するか）
  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return REQUIRED_FIELD_LABEL_KEYS.every((key) => key in wordList.value);
  });

  const groupRadioItemsAll = OperationGroupList.other as RadioItem[];
  const authtypeRadioItemsAll = OperationAuthTypeList.all as RadioItem[];

  /**
   * 初期処理
   */
  const initialize = async (): Promise<void> => {
    shopListInput.chain_code = localStorageGetChainCode();
    shopListInput.customer_code = localStorageGetCustomerCode();
    cognitoUserCountInput.customer_code = localStorageGetCustomerCode();
    chainSelectValues.value = { chain_code: "", name: "" };
    shopSelectValues.value = { shop_code: "", name: "" };
  };

  /**
   * オペレータ編集データをセットする
   */
  const updateOperatorData = async (
    operatorInfo: ApolloQueryResult<GetCognitoUserQuery>
  ): Promise<void> => {
    cognitUserCreateInput.user_id = operatorStore.user_id;

    //オペレータ名称
    if (operatorInfo.data.getCognitoUser?.name !== undefined) {
      cognitUserCreateInput.name = operatorInfo.data.getCognitoUser?.name;
    }
    //所属グループ
    if (operatorInfo.data.getCognitoUser?.group !== undefined) {
      cognitUserCreateInput.group = operatorInfo.data.getCognitoUser?.group;
      const found = groupRadioItemsAll.find(
        (element) => element.index === cognitUserCreateInput.group
      );
      if (found) {
        groupSelectValues.value = found;
        //選択する
        onClickGroup(groupSelectValues.value.index);
      }
    }
    //所属グループ名
    selectedOperatorGroupName.value =
      operatorInfo.data.getCognitoUser?.group_name;
    //所属チェーン名
    if (
      operatorInfo.data.getCognitoUser?.customer_code !== undefined &&
      operatorInfo.data.getCognitoUser?.chain_code !== undefined
    ) {
      //チェーン情報を取得する
      const result = await getChainInfoData(
        operatorInfo.data.getCognitoUser.customer_code,
        operatorInfo.data.getCognitoUser.chain_code
      );
      if (result?.name !== undefined) {
        selectedOperatorChainName.value = result.name;
      }
      if (result?.chain_code !== undefined) {
        cognitUserCreateInput.chain_code = result.chain_code;
        cognitUserCreateInput.operator_column_tel = result.operator_column_tel;

        //IP制限の有無を判定
        isIpRestrictedChain.value = checkIpRestricted(result.chain_code);

        //本部権限を選択した場合、編集モードでもE-mail入力欄を表示する
        if (
          groupSelectValues.value &&
          isOperatorGroupAdmin(groupSelectValues.value.index)
        ) {
          email_required.value = 1;
          //IP制限ありチェーン: E-mail項目として表示・更新可能（Cognitoのemail属性を更新）
          //IP制限なしチェーン: 認証用E-mail項目は編集不可（Cognitoのemail属性を更新しない）
          if (isIpRestrictedChain.value) {
            cognitUserCreateInput.operator_column_email = 1;
          } else {
            cognitUserCreateInput.operator_column_email = 0;
          }
        } else {
          cognitUserCreateInput.operator_column_email =
            result.operator_column_email;
        }
      }
    }

    //店舗
    if (operatorInfo.data.getCognitoUser?.shop_code !== undefined) {
      cognitUserCreateInput.shop_code =
        operatorInfo.data.getCognitoUser?.shop_code;
      shopSelectValues.value = (shopListItems.value || [])!.find(
        (element) =>
          element &&
          element.shop_code === operatorInfo.data.getCognitoUser?.shop_code
      ) as { shop_code: string; name: string } | null | undefined;
    }
    //権限
    if (operatorInfo.data.getCognitoUser?.auth_type !== undefined) {
      cognitUserCreateInput.auth_type =
        operatorInfo.data.getCognitoUser?.auth_type;
      const found = authtypeRadioItemsAll.find(
        (element) => element.index === cognitUserCreateInput.auth_type
      );
      if (found) {
        authtypeSelectValues.value = found;
      }
    }
    //個人情報閲覧
    if (operatorInfo.data.getCognitoUser?.personal_info !== undefined) {
      cognitUserCreateInput.personal_info =
        operatorInfo.data.getCognitoUser?.personal_info;
      const found = personalRadioItems.find(
        (element: { index: string }) =>
          element.index === operatorInfo.data.getCognitoUser?.personal_info
      );
      if (found) {
        personalSelectValues.value = found;
      }
    }
    //個人情報ダウンロード
    if (operatorInfo.data.getCognitoUser?.personal_info_dl !== undefined) {
      cognitUserCreateInput.personal_info_dl =
        operatorInfo.data.getCognitoUser?.personal_info_dl;
      const found = personalRadioItems.find(
        (element: { index: string }) =>
          element.index === operatorInfo.data.getCognitoUser?.personal_info_dl
      );
      if (found) {
        personalDlSelectValues.value = found;
      }
    }
    //email
    if (operatorInfo.data.getCognitoUser?.email !== undefined) {
      cognitUserCreateInput.email = operatorInfo.data.getCognitoUser?.email;
      update_mode_operator_email.value = cognitUserCreateInput.email
        ? cognitUserCreateInput.email
        : "";
    }
    //電話番号
    if (operatorInfo.data.getCognitoUser?.phone_number !== undefined) {
      cognitUserCreateInput.phone_number =
        operatorInfo.data.getCognitoUser?.phone_number;
    }

    syncSelectionFields();

    // パスワードフィールドの初期化（編集モード）- 初回のみ実行
    // syncSelectionFieldsから分離して、権限タイプ変更時にパスワードがリセットされないようにする
    if (operatorFormRef.value) {
      // パスワード（編集モード）- 空でもOK（requiredではない）
      operatorFormRef.value.setFieldValue("authPasswordEdit", "", false);
      // パスワード確認（編集モード）- 空でもOK（requiredではない）
      operatorFormRef.value.setFieldValue("authPasswordConfirmEdit", "", false);
    }
  };

  /**
   * 初期ロード実行（onMounted処理の委譲先）
   */
  async function runInitialLoad(): Promise<void> {
    try {
      overlay.value = true;

      //初期化処理
      await initialize();

      //文言データを取得する
      wordList.value = await getWordListData(
        localStorageGetChainCode(),
        OPERATOR_REGISTER_FILE_NAME,
        localStorageGetCustomerCode()
      );

      //編集モードの場合、対象のオペレータを取得する
      let operatorInfo: ApolloQueryResult<GetCognitoUserQuery> | null = null;
      if (operatorStore.hasValue) {
        const result = await getCognitoUserData(
          {
            chain_code: operatorStore.chain_code,
            customer_code: localStorageGetCustomerCode(),
            user_id: operatorStore.user_id,
          },
          {
            area_code: 10,
            detail: "情報取得",
            display_category: "オペレーター",
            operation: "情報取得",
            user_id: localStorageGetUserId(),
          }
        );
        operatorInfo = result;

        //編集モードの場合、既存のチェーンコードを設定する
        await setChainCode(operatorStore.chain_code, true);
        //チェーンリストは表示しない
        chainViewFlag.value = false;
        //店舗情報を取得する
        shopListItems.value = await getShopListData(shopListInput) ?? [];
      } else if (checkChainListDisplay(localStorageGetAuthType())) {
        //運用管理者の場合、チェーン情報を取得する
        chainListItems.value = await getChainListData(localStorageGetCustomerCode()) ?? [];
      } else {
        //運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
        await setChainCode(localStorageGetChainCode(), true);
        //チェーンリストは表示しない
        chainViewFlag.value = false;
        //店舗情報を取得する
        shopListItems.value = await getShopListData(shopListInput) ?? [];
      }
      //所属グループのラジオボタンを反映する
      await createGroupRadio();

      //権限のラジオボタンを反映する
      onClickGroup(groupSelectValues.value.index);
      updateMode.value = false;

      // 一覧から遷移された場合、編集情報をセットする
      if (operatorStore.hasValue) {
        if (operatorInfo !== null && operatorInfo !== undefined) {
          updateMode.value = true;
          await updateOperatorData(operatorInfo);
          operatorStore.setupValue({ user_id: "", chain_code: "" });
        }
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }

      return;
    } finally {
      overlay.value = false;
    }
  }

  return {
    isWordListReady,
    initialize,
    runInitialLoad,
  };
};
